/** ProfileDetailsPage — edit the personal information for the signed-in customer. */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Check, User, BookmarkCheck } from 'lucide-react';
import { Container, PageHeader } from '@/components/layout/Container';
import { Button, Card, Input, Spinner } from '@/components/ui';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { useCustomerAuth } from '@/context/CustomerAuthContext';

export function ProfileDetailsPage() {
  const { status, customer, updateProfile } = useCustomerAuth();

  if (status === 'loading') {
    return (
      <Container className="grid min-h-[60vh] place-items-center">
        <Spinner size="lg" className="text-primary-600" />
      </Container>
    );
  }

  if (status !== 'authenticated') {
    return (
      <Container className="max-w-md py-10">
        <Card className="p-8 text-center">
          <User size={30} className="mx-auto mb-4 text-primary-600" />
          <h1 className="text-xl font-extrabold text-ink">Sign in to view your profile details</h1>
          <div className="mt-6 flex justify-center">
            <GoogleSignInButton />
          </div>
        </Card>
      </Container>
    );
  }

  return <ProfileDetailsForm customer={customer} updateProfile={updateProfile} />;
}

function ProfileDetailsForm({ customer, updateProfile }) {
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    reset,
  } = useForm({ defaultValues: { name: customer.name ?? '', mobile: customer.mobile ?? '' } });

  const onSave = async (values) => {
    setError(null);
    try {
      await updateProfile(values);
      reset(values);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.message ?? 'Could not save. Please try again.');
    }
  };

  return (
    <Container className="max-w-2xl py-6">
      <PageHeader title="Profile Details" subtitle="Manage your personal information" />

      {/*
        Explains WHY the form is worth filling in. It is the first thing a new
        student sees after signing up, and "fill this in" without a reason is
        the easiest screen in the app to skip.
      */}
      <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
        <BookmarkCheck size={18} className="mt-0.5 shrink-0 text-primary-600" />
        <span>
          <span className="font-bold">Fill once, save for next time.</span> Your name and mobile are
          reused at checkout, so ordering later takes a couple of taps.
        </span>
      </div>

      <Card className="p-5">
        <form onSubmit={handleSubmit(onSave)} className="space-y-4">
          <Input
            label="Full name"
            error={errors.name?.message}
            {...register('name', { required: 'Name is required' })}
          />
          <Input label="Email" value={customer.email} disabled hint="From your Google account" />
          <Input
            label="Mobile number"
            type="tel"
            placeholder="10-digit mobile"
            error={errors.mobile?.message}
            {...register('mobile', {
              pattern: { value: /^[0-9]{10}$/, message: 'Enter a 10-digit mobile number' },
            })}
          />
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-danger">{error}</p>}
          <div className="flex items-center gap-3">
            <Button type="submit" isLoading={isSubmitting} disabled={!isDirty}>
              Save changes
            </Button>
            {saved && (
              <span className="inline-flex items-center gap-1 text-sm font-medium text-success">
                <Check size={16} /> Saved
              </span>
            )}
          </div>
        </form>
      </Card>
    </Container>
  );
}

export default ProfileDetailsPage;
